import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Wallet as WalletIcon, Plus, ArrowDownLeft, CircleCheck, CircleX, Hourglass, RefreshCw, Smartphone } from "lucide-react";
import { getToken } from "../api";
import { LOCAL_API } from "../config";
import { useAuth } from "../auth";
import { Spinner } from "../components/cards";
import { SignInNotice } from "./Account";

const localReq = async (method, path, body) => {
  const headers = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${LOCAL_API}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = res.status !== 204 ? await res.json().catch(() => ({})) : {};
  if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : data.message || `Request failed (${res.status})`);
  return data;
};

const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-white/10 bg-white/[0.02] p-6 ${className}`}>{children}</div>
);

const gbp = (n) => `£${(Number(n) || 0).toFixed(2)}`;

const statusMeta = (s) =>
  s === "paid" ? { icon: CircleCheck, cls: "text-emerald-400", label: "Paid" }
  : s === "pending" ? { icon: Hourglass, cls: "text-amber-400", label: "Pending" }
  : { icon: CircleX, cls: "text-crimson", label: (s || "failed").charAt(0).toUpperCase() + (s || "failed").slice(1) };

export const Wallet = () => {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [wallet, setWallet] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null); // {tone, text}
  const polling = useRef(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [w, d] = await Promise.all([localReq("GET", "/wallet"), localReq("GET", "/wallet/transactions")]);
      setWallet(w);
      setDeposits(Array.isArray(d) ? d : []);
    } catch {}
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  // Handle return from Stripe Checkout: poll the deposit status until paid, then refresh balance.
  useEffect(() => {
    const sessionId = params.get("session_id");
    const deposited = params.get("deposited");
    const cancelled = params.get("cancelled");
    if (!user) return;
    if (cancelled) {
      setBanner({ tone: "err", text: "Deposit cancelled — nothing was added to your wallet." });
      setParams({}, { replace: true });
      return;
    }
    if (sessionId && deposited) {
      setBanner({ tone: "info", text: "Confirming your deposit…" });
      let tries = 0;
      polling.current = setInterval(async () => {
        tries += 1;
        try {
          const st = await localReq("GET", `/wallet/deposit/status/${sessionId}`);
          if (st.payment_status === "paid") {
            clearInterval(polling.current);
            setBanner({ tone: "ok", text: `Deposit successful — ${gbp(st.amount)} added to your wallet.` });
            setParams({}, { replace: true });
            load();
          } else if (tries > 20) {
            clearInterval(polling.current);
            setBanner({ tone: "err", text: "We couldn't confirm your deposit. If you paid, it will appear shortly." });
            setParams({}, { replace: true });
            load();
          }
        } catch {
          if (tries > 20) clearInterval(polling.current);
        }
      }, 2000);
      return () => clearInterval(polling.current);
    }
  }, [user, params, setParams, load]);

  useEffect(() => () => clearInterval(polling.current), []);

  if (!ready) return <Spinner />;
  if (!user) return <SignInNotice what="open your wallet and make a deposit" />;

  const startDeposit = async (e) => {
    e.preventDefault();
    const val = parseFloat(amount);
    if (!val || val <= 0) {
      setBanner({ tone: "err", text: "Enter a valid amount." });
      return;
    }
    setBusy(true);
    setBanner(null);
    try {
      const r = await localReq("POST", "/wallet/deposit", { amount: val, origin_url: window.location.origin });
      window.location.href = r.checkout_url;
    } catch (err) {
      setBanner({ tone: "err", text: err.message || "Could not start the deposit." });
      setBusy(false);
    }
  };

  const toneCls = { ok: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400", err: "border-crimson/30 bg-crimson/10 text-crimson", info: "border-white/15 bg-white/[0.04] text-slate-300" };

  return (
    <div className="mx-auto max-w-2xl" data-testid="wallet-page">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-crimson/15 text-crimson"><WalletIcon size={20} /></span>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-white">Wallet</h1>
          <p className="text-sm text-slate-500">Your Michael C Streamline balance.</p>
        </div>
      </div>

      {banner && (
        <p className={`mb-5 rounded-xl border px-4 py-3 text-sm ${toneCls[banner.tone]}`} data-testid="wallet-banner">{banner.text}</p>
      )}

      <Card className="mb-6 text-center" data-testid="wallet-balance-card">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Balance</p>
        <p className="mt-2 font-heading text-5xl font-black text-white" data-testid="wallet-balance">
          {wallet ? gbp(wallet.balance) : "—"}
        </p>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">GBP</p>
        <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
          <button onClick={() => document.getElementById("deposit-amount")?.focus()} data-testid="wallet-deposit-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-crimson px-6 py-3 font-heading text-sm font-bold text-white transition-colors hover:bg-crimson-hover">
            <Plus size={16} /> Deposit
          </button>
          <button disabled title="Withdrawals are coming soon" data-testid="wallet-withdraw-button" className="inline-flex cursor-not-allowed items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3 font-heading text-sm font-bold text-slate-500">
            <ArrowDownLeft size={16} /> Withdraw · Coming soon
          </button>
        </div>
      </Card>

      <Card className="mb-6" data-testid="deposit-form-card">
        <h2 className="mb-1 font-heading text-lg font-bold tracking-tight text-white">Deposit funds</h2>
        <p className="mb-4 text-sm text-slate-500">Pay securely by card, Apple Pay or Google Pay. Test mode — no real money moves.</p>
        <form onSubmit={startDeposit} className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">£</span>
            <input
              id="deposit-amount"
              type="number"
              min="1"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Amount"
              data-testid="deposit-amount"
              className="w-full rounded-xl border border-white/10 bg-white/[0.04] py-3 pl-8 pr-4 text-sm text-white placeholder-slate-600 outline-none focus:border-crimson/60"
            />
          </div>
          <button type="submit" disabled={busy} data-testid="deposit-submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-crimson px-6 py-3 font-heading text-sm font-bold text-white transition-colors hover:bg-crimson-hover disabled:opacity-50">
            {busy ? "Redirecting…" : "Pay with Stripe"}
          </button>
        </form>
      </Card>

      <div>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold tracking-tight text-white">Wallet history</h2>
          <button onClick={load} className="text-slate-500 transition-colors hover:text-white" title="Refresh" data-testid="wallet-refresh"><RefreshCw size={16} /></button>
        </div>
        {deposits.length === 0 ? (
          <Card><p className="text-sm text-slate-500" data-testid="wallet-no-deposits">No activity yet.</p></Card>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-white/10" data-testid="deposit-history">
            {deposits.map((d, i) => {
              const m = statusMeta(d.status);
              const Icon = m.icon;
              return (
                <div key={d.id || i} className="flex items-center justify-between gap-3 border-b border-white/5 px-4 py-3 last:border-0" data-testid={`deposit-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/[0.05] text-emerald-400"><Plus size={15} /></span>
                    <div>
                      <p className="font-heading text-sm font-semibold text-white">{gbp(d.amount)}</p>
                      <p className="text-xs text-slate-500">{d.label || "Deposit"} · {d.created_at ? new Date(d.created_at).toLocaleString() : ""}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider ${m.cls}`} data-testid={`deposit-status-${i}`}>
                    <Icon size={13} /> {m.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button onClick={() => { window.location.href = "mcstreamline://wallet"; }} data-testid="wallet-return-to-app" className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-heading text-xs font-bold text-slate-300 transition-colors hover:border-crimson/50 hover:text-white">
        <Smartphone size={14} /> Return to app
      </button>
    </div>
  );
};

export default Wallet;
